import React from "react";
import StatComparison, { PossessionBar } from "./StatComparison";
import { OVERVIEW_KEYS, buildStatComparison, eventMinuteLabel, isCard } from "./lineupModel";
import { useMatchStats } from "./useMatchData";
import "./football-app.css";

const SOURCE = "Source: API-Football match statistics.";
const RECORDED_SOURCE = "Source: PitchAPI · recorded match statistics. Possession is a provider pass-share estimate.";
// Keep the source sample intact: never fill absent observations with zeroes or
// infer shots on target from the provider's unreconciled shot flags.
export function recordedBasicTeams(recorded) {
  if (!recorded?.actuals?.home?.values || !recorded?.actuals?.away?.values) return null;
  const keys = {
    "expected_goals": "xgf", "Total Shots": "shots_for", "Corner Kicks": "corners_for",
    "Total passes": "passes_for", "Passes %": "passing.pass_accuracy",
    "Fouls": "fouls_for", "Yellow Cards": "yellow_cards_for", "Red Cards": "red_cards_for",
    "Ball Possession": "territory.possession_pct",
  };
  return ["home", "away"].map(side => ({statistics: Object.entries(keys)
    .filter(([, key]) => Number.isFinite(recorded.actuals?.[side]?.values?.[key]))
    .map(([type, key]) => ({type, value: recorded.actuals[side].values[key]}))}));
}

/**
 * Goals are already in the match header, so Overview only adds what the header
 * can't: sendings-off. The full feed lives in Timeline.
 */
export function KeyEvents({ events, homeTeamId, homeName, awayName }) {
  const reds = (events || []).filter((e) => isCard(e) && /red|second/i.test(e.detail || ""));
  if (!reds.length) return null;
  return (
    <section className="fa-card" aria-label="Sendings-off">
      <h3>Sent off</h3>
      <ul className="fa-list">
        {reds.map((e, i) => (
          <li key={i}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 2px" }}>
              <span className="fa-min" style={{ width: 40, textAlign: "center" }}>{eventMinuteLabel(e)}</span>
              <span aria-hidden="true">🟥</span>
              <span><b>{e.player_name || "—"}</b><span className="fa-sub" style={{ display: "block" }}>{Number(e.team_id) === Number(homeTeamId) ? homeName : awayName}</span></span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Overview facts: possession and the four headline numbers. */
export function OverviewStats({ fixtureId, homeName, awayName, onSeeAll, statsState, recorded }) {
  const own = useMatchStats(statsState || recordedBasicTeams(recorded) ? null : fixtureId);
  const state = statsState || own;
  const fallback = recordedBasicTeams(recorded);
  const { loading } = state;
  const teams = fallback || state.teams;
  if (loading && !teams) return <div className="fa-skeleton" role="status" aria-label="Loading match statistics" />;
  if (!teams) return null;
  const { possession, rows } = buildStatComparison(teams[0], teams[1]);
  const headline = rows.filter((r) => OVERVIEW_KEYS.includes(r.key));
  if (!possession && !headline.length) return null;
  return (
    <section className="fa-card" aria-label="Match statistics summary">
      <h3>Match facts</h3>
      <PossessionBar possession={possession} />
      <StatComparison rows={headline} homeName={homeName} awayName={awayName} />
      {onSeeAll && <button type="button" className="fa-btn" style={{ width: "100%", marginTop: 8 }} onClick={onSeeAll}>All match statistics</button>}
      <p className="fa-note">{fallback || state.source === "pitchapi" ? RECORDED_SOURCE : SOURCE}</p>
    </section>
  );
}

/** Stats tab: every recorded comparison row. */
export function FullStats({ fixtureId, homeName, awayName, recorded }) {
  const state = useMatchStats(recordedBasicTeams(recorded) ? null : fixtureId);
  const fallback = recordedBasicTeams(recorded);
  const teams = fallback || state.teams;
  const { loading, error } = state;
  if (loading && !teams) return <div className="fa-skeleton" role="status" aria-label="Loading match statistics" style={{ height: 240 }} />;
  if (!teams) return <div className="fa-empty">{error || "Match statistics are published once the match is under way."}</div>;
  const { possession, rows } = buildStatComparison(teams[0], teams[1]);
  return (
    <section className="fa-card" aria-label="Match statistics">
      <h3>Match statistics</h3>
      <PossessionBar possession={possession} />
      <StatComparison rows={rows} homeName={homeName} awayName={awayName} />
      <p className="fa-note">{fallback || state.source === "pitchapi" ? RECORDED_SOURCE : SOURCE} Statistics the provider did not record are left out, not shown as zero.</p>
    </section>
  );
}
